import { evidenceLinkedTypeLabel, formatEvidenceRowId } from './evidenceBrowserLabels'

import { formatSydneyDateTime, sydneyYmd, toSydneyDatetimeLocal } from './sydneyDateTime'
/** Local calendar date YYYY-MM-DD (incident day comparisons). */
export function toLocalDateYmd(value) {
  if (!value) return null
  const d = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(d.getTime())) return null
  return sydneyYmd(d)
}

export function parseActivitySortTime(item, fallbackIso) {
  if (!item || typeof item !== 'object') return fallbackIso ? new Date(fallbackIso).getTime() : 0
  const raw =
    item.observed_at ||
    item.SnapShotTime ||
    item.StartTime ||
    item.intervalstart ||
    item.IntervalStart ||
    item.timestamp ||
    item.TransactionTime ||
    item._evidence_event_time ||
    item.gaming_date ||
    item.trading_day ||
    fallbackIso
  if (!raw) return 0
  if (typeof raw === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    const t = new Date(`${raw}T12:00:00`).getTime()
    return Number.isFinite(t) ? t : 0
  }
  const normalized = typeof raw === 'string' && !raw.includes('T') ? String(raw).replace(' ', 'T') : raw
  const ms = new Date(normalized).getTime()
  return Number.isFinite(ms) ? ms : 0
}

export function activityStableKey(item) {
  if (!item || typeof item !== 'object') return null
  const itemType = item.type || item._evidence_item_type || item.evidence_category
  if (itemType === 'observation') {
    const obsId = item._evidence_source_id || item.observation_id
    if (obsId) return `observation:${obsId}`
  }
  const payoutId = item.payout_id || item.PayoutID || item.payoutId
  if (payoutId != null && String(payoutId).trim()) return `payout:${String(payoutId).trim()}`
  if (itemType === 'combined_interval') {
    const spk = item.sessionpk || item.SessionPK || item.session_id
    const iv = item.intervalstart || item.IntervalStart || item.SnapShotTime
    if (spk && iv) return `interval:${spk}:${String(iv).slice(0, 19)}`
  }
  if (itemType === 'combined_session' || itemType === 'session') {
    const spk = item.sessionpk || item.SessionPK || item.session_id
    if (spk) return `session:${spk}`
  }
  const pk =
    item.TransactionPK ??
    item.transaction_id ??
    item.SessionPK ??
    item.session_id ??
    item._evidence_source_id ??
    item.source_id ??
    item.id
  if (pk != null && String(pk).trim()) return `pk:${String(pk).trim()}`
  return null
}

export function resolveActivityEntryType(item) {
  if (item?._evidence_item_type === 'observation') return 'observation'
  const label = evidenceLinkedTypeLabel(item)
  if (label === 'Payout') return 'payout'
  if (label === 'Session') return 'session'
  if (label === 'Interval') return 'interval'
  if (label === 'Slot jackpot') return 'jackpot'
  if (label === 'Transaction') return 'transaction'
  return 'event'
}

function formatTimelineTime(ms) {
  if (!ms) return '—'
  return formatSydneyDateTime(new Date(ms))
}

function formatTimelineAmount(item) {
  const n = { ...item }
  const isSession = Boolean(n.SessionPK || n.session_id) && resolveActivityEntryType(n) === 'session'
  const isInterval = resolveActivityEntryType(n) === 'interval'
  const amountRaw =
    n.CashOut ??
    n.cash_out ??
    n.amount ??
    n.jackpot_win_aud ??
    n.CreditWin ??
    n.creditwin_aud ??
    n.CashIn ??
    n.cash_in ??
    n.Turnover ??
    n.turnover ??
    0
  const amountNum = Number(amountRaw) || 0
  const amount =
    amountNum >= 10000 && (isSession || isInterval) ? amountNum / 100 : amountNum
  if (!amount) return null
  return amount
}

export function resolveActivityScope(sortMs, incidentDayYmd) {
  if (!incidentDayYmd || !sortMs) return 'prior_visit'
  const rowDay = toLocalDateYmd(new Date(sortMs))
  return rowDay === incidentDayYmd ? 'incident_day' : 'prior_visit'
}

export function suggestStepIdForEntry(entryType, steps) {
  if (!Array.isArray(steps) || !steps.length) return null
  const find = (keywords) =>
    steps.find((s) => keywords.some((k) => String(s.title || '').toLowerCase().includes(k)))?.id ?? null
  if (entryType === 'payout') return find(['payout'])
  if (entryType === 'observation') return find(['cctv', 'staff', 'gaming', 'session'])
  if (entryType === 'session' || entryType === 'interval' || entryType === 'transaction' || entryType === 'jackpot') {
    return find(['session', 'gaming'])
  }
  return null
}

export function buildTimelineEntryFromPatronActivity(activity, { incidentDayYmd, steps } = {}) {
  if (!activity || typeof activity !== 'object') return null
  const payload = activity.payload || {}
  const sortMs = parseActivitySortTime({ observed_at: activity.sort_time, ...payload }, activity.sort_time)
  const egmFrom = activity.egm || payload.EgmHouseNo || null
  const egmTo = activity.egm_to || payload.egm_to || null
  const egm =
    egmFrom && egmTo && String(egmFrom) !== String(egmTo)
      ? `${egmFrom} → ${egmTo}`
      : egmFrom || egmTo || '—'
  const entryType = activity.entry_type === 'observation' ? 'observation' : activity.entry_type || 'observation'
  return {
    id: `pa:${activity.id}`,
    stableKey: `pa:${activity.id}`,
    patronActivityId: activity.id,
    sortMs,
    timeLabel: formatTimelineTime(sortMs),
    dayYmd: sortMs ? toLocalDateYmd(new Date(sortMs)) : null,
    entryType,
    typeLabel: entryType === 'observation' ? 'Observation' : entryType,
    scope: activity.scope || resolveActivityScope(sortMs, incidentDayYmd),
    status: activity.status || 'confirmed',
    ownership: 'owned',
    egm,
    egmFrom,
    egmTo,
    memberId: null,
    amount: null,
    linkReason: null,
    observationText: activity.observation_text || payload.observation_text || null,
    media: Array.isArray(payload.media) ? payload.media[0] || null : payload.media || null,
    mediaList: Array.isArray(payload.media)
      ? payload.media.filter(Boolean)
      : payload.media
        ? [payload.media]
        : [],
    observationId: activity.id,
    relatedSourceId: payload.related_source_id || null,
    connectedStepId:
      activity.connected_step_id ??
      payload._connected_step_id ??
      suggestStepIdForEntry(entryType, steps),
    linkedEvidence: Array.isArray(activity.linked_evidence) ? activity.linked_evidence : [],
    sourceItem: null,
    browseItem: null,
  }
}

export function buildTimelineEntryFromEvidence(item, { incidentDayYmd, fallbackDate, steps } = {}) {
  if (!item || typeof item !== 'object') return null
  if (item._evidence_item_type === 'observation') return null
  const sortMs = parseActivitySortTime(item, fallbackDate)
  const entryType = resolveActivityEntryType(item)
  const key = activityStableKey(item)
  const egm = item.EgmHouseNo || item.egm_house_no || item.egm_pk || item.game_id || '—'
  const member = item.MemberID || item.member_id || item.membership_number || '—'
  const payloadStatus = item.activity_status || item._activity_status
  const status = payloadStatus === 'suggested' ? 'suggested' : 'confirmed'
  const connectedStepId =
    item._connected_step_id ??
    item.connected_step_id ??
    item.payload?._connected_step_id ??
    suggestStepIdForEntry(entryType, steps)
  return {
    id: key || formatEvidenceRowId(item),
    stableKey: key,
    sortMs,
    timeLabel: formatTimelineTime(sortMs),
    dayYmd: sortMs ? toLocalDateYmd(new Date(sortMs)) : null,
    entryType,
    typeLabel: evidenceLinkedTypeLabel(item),
    scope: resolveActivityScope(sortMs, incidentDayYmd),
    status,
    ownership: 'owned',
    egm,
    egmFrom: egm !== '—' ? egm : null,
    egmTo: null,
    memberId: member === '__UNCARDED__' ? 'Uncarded' : member,
    amount: formatTimelineAmount(item),
    linkReason: item._link_reason || null,
    observationText: null,
    media: null,
    observationId: null,
    relatedSourceId: null,
    connectedStepId,
    linkedEvidence: [],
    sourceItem: item,
    browseItem: null,
  }
}

export function buildTimelineEntryFromSuggestion(browseItem, { incidentDayYmd, steps } = {}) {
  if (!browseItem || typeof browseItem !== 'object') return null
  const sortMs = parseActivitySortTime(browseItem)
  const entryType =
    browseItem.type === 'combined_interval'
      ? 'interval'
      : browseItem.type === 'combined_session' || browseItem.type === 'session'
        ? 'session'
        : browseItem.type === 'payout'
          ? 'payout'
          : browseItem.type === 'slot_jackpot'
            ? 'jackpot'
            : 'transaction'
  const key = activityStableKey({ ...browseItem, type: browseItem.type }) || browseItem.id
  return {
    id: key || browseItem.id,
    stableKey: key,
    sortMs,
    timeLabel: formatTimelineTime(sortMs),
    dayYmd: sortMs ? toLocalDateYmd(new Date(sortMs)) : null,
    entryType,
    typeLabel: entryType === 'interval' ? 'Interval' : entryType === 'session' ? 'Session' : entryType,
    scope: resolveActivityScope(sortMs, incidentDayYmd),
    status: 'suggested',
    ownership: 'owned',
    egm: browseItem.HouseNumber || browseItem.EgmHouseNo || browseItem.EgmPK || '—',
    memberId: browseItem.membership_number || browseItem.MemberID || browseItem.member_id || '—',
    amount: formatTimelineAmount(browseItem),
    linkReason: null,
    connectedStepId: suggestStepIdForEntry(entryType, steps),
    sourceItem: null,
    browseItem,
  }
}

export function mergePatronActivityTimeline(confirmedEntries, suggestedEntries, dismissedKeys) {
  const dismissed = dismissedKeys instanceof Set ? dismissedKeys : new Set(dismissedKeys || [])
  const confirmedKeys = new Set(confirmedEntries.map((e) => e.stableKey).filter(Boolean))
  const merged = [...confirmedEntries]
  for (const s of suggestedEntries) {
    if (!s?.stableKey || dismissed.has(s.stableKey)) continue
    if (confirmedKeys.has(s.stableKey)) continue
    merged.push(s)
  }
  merged.sort((a, b) => (a.sortMs || 0) - (b.sortMs || 0))
  return merged
}

const DISMISSED_STORAGE_PREFIX = 'patron-activity-dismissed:'

export function loadDismissedSuggestionKeys(caseId) {
  if (!caseId) return new Set()
  try {
    const raw = localStorage.getItem(`${DISMISSED_STORAGE_PREFIX}${caseId}`)
    const arr = raw ? JSON.parse(raw) : []
    return new Set(Array.isArray(arr) ? arr : [])
  } catch {
    return new Set()
  }
}

export function saveDismissedSuggestionKeys(caseId, keysSet) {
  if (!caseId) return
  try {
    localStorage.setItem(`${DISMISSED_STORAGE_PREFIX}${caseId}`, JSON.stringify([...keysSet]))
  } catch {
    /* ignore quota */
  }
}

export function filterTimelineByScope(entries, scopeFilter) {
  if (!scopeFilter || scopeFilter === 'all') return entries
  if (scopeFilter === 'incident_day') return entries.filter((e) => e.scope === 'incident_day')
  if (scopeFilter === 'prior_visits') return entries.filter((e) => e.scope === 'prior_visit')
  return entries
}

export const LAYOUT_VIEWS = [
  { id: 'timeline', label: 'Timeline' },
  { id: 'by_egm', label: 'By EGM' },
  { id: 'by_type', label: 'By type' },
]

function normalizeEgmGroupKey(entry) {
  const from = entry.egmFrom || (entry.egm && !String(entry.egm).includes('→') ? entry.egm : null)
  return from && from !== '—' ? String(from) : 'No EGM'
}

const TYPE_GROUP_LABELS = {
  observation: 'Observations',
  session: 'Sessions',
  interval: 'Intervals',
  payout: 'Payouts',
  transaction: 'Transactions',
  jackpot: 'Jackpots',
  event: 'Other',
}

export function groupTimelineForLayout(entries, layoutView) {
  if (!layoutView || layoutView === 'timeline') {
    return [{ key: 'timeline', label: null, entries }]
  }
  if (layoutView === 'by_egm') {
    const groups = new Map()
    for (const entry of entries) {
      const key = normalizeEgmGroupKey(entry)
      if (!groups.has(key)) groups.set(key, [])
      groups.get(key).push(entry)
    }
    return [...groups.entries()]
      .sort(([a], [b]) => a.localeCompare(b, undefined, { numeric: true }))
      .map(([key, groupEntries]) => ({
        key,
        label: key === 'No EGM' ? 'No EGM specified' : `EGM ${key}`,
        entries: [...groupEntries].sort((a, b) => (a.sortMs || 0) - (b.sortMs || 0)),
      }))
  }
  if (layoutView === 'by_type') {
    const groups = new Map()
    for (const entry of entries) {
      const key = entry.entryType || 'event'
      if (!groups.has(key)) groups.set(key, [])
      groups.get(key).push(entry)
    }
    const order = ['observation', 'interval', 'session', 'payout', 'transaction', 'jackpot', 'event']
    return [...groups.entries()]
      .sort(([a], [b]) => order.indexOf(a) - order.indexOf(b))
      .map(([key, groupEntries]) => ({
        key,
        label: TYPE_GROUP_LABELS[key] || key,
        entries: [...groupEntries].sort((a, b) => (a.sortMs || 0) - (b.sortMs || 0)),
      }))
  }
  return [{ key: 'timeline', label: null, entries }]
}

export function toDatetimeLocalValue(date) {
  const d = date instanceof Date ? date : new Date(date)
  if (Number.isNaN(d.getTime())) return ''
  const pad = (n) => String(n).padStart(2, '0')
  return toSydneyDatetimeLocal(d)
}
